import { enterBootHumanWait } from '#~/routes/root/boot-budget.js';
import { useEffect } from 'react';
import { ElectronAPI } from '#~/electron/api.js';
import { getElectronUrl } from '#~/electron/utils.js';
import {
  beginAuthJourney,
  buildHostedAuth0ExtParams,
  setActiveAuthJourney
} from '#~/auth/index.js';
import { createAuthJourneyTracker } from '#~/analytics/domains/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { Button } from '@studio/design-system/button';
import { Centered } from '@studio/design-system/overlay';
import {
  getCurrentVendor,
  getCurrentVendorLogo,
  vendorName
} from '@studio/design-system/themes/vendor.js';
import { clsx } from 'cn';
import type { ReactElement } from 'react';

// ENG-3451: the standard multicolor Google "G" mark, inlined so the login
// screen needs no new asset pipeline or design-system icon. Decorative only
// (the button text carries the meaning), hence aria-hidden.
const GoogleMark = (): ReactElement => (
  <svg
    aria-hidden='true'
    className='h-16 w-16'
    data-stag='electron.login.google_mark'
    viewBox='0 0 24 24'
  >
    <path
      d='M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z'
      fill='#4285F4'
    />
    <path
      d='M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.98 11.98 0 0 0 12 24z'
      fill='#34A853'
    />
    <path
      d='M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z'
      fill='#FBBC05'
    />
    <path
      d='M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.97 11.97 0 0 0 12 0 11.98 11.98 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z'
      fill='#EA4335'
    />
  </svg>
);

export const ElectronLogin = (): ReactElement => {
  // This screen is rendered above BootOverlay, including while the hosted
  // sign-in window is open. Its unmount resumes bounded machine work.
  useEffect(() => enterBootHumanWait(), []);
  const { track } = useAnalytics();
  const authJourneyTracker = createAuthJourneyTracker(track);

  return (
    <Centered>
      <div className='flex flex-col items-center'>
        <img src={getCurrentVendorLogo()} alt={vendorName()} className='w-[236px]' />
        <div className='mt-64 flex flex-col items-center gap-16 text-center'>
          <div>
            <h1 className='font-demibold'>Log in</h1>
            <p className='px-12 text-muted'>Welcome back! Please login to your account</p>
          </div>

          <div className='my-6' />

          {/* ENG-3451: visible Google sign-in for the desktop app. Routes
           * through the existing ElectronAPI.login({ type: 'google' }) flow
           * (main process already supports it) with the same auth-journey
           * analytics shape as the email path. Secondary variant so the
           * pre-existing email primary action keeps its visual weight. No
           * `block` here: it would override the Button's flex row and stack
           * the Google mark above the label. */}
          <Button
            className='w-full'
            data-stag='electron.login.continue_with_google'
            size='lg'
            variant='secondary'
            onClick={() => {
              const journey = beginAuthJourney('studio_login');
              const hostedAuthParams = buildHostedAuth0ExtParams(journey);
              setActiveAuthJourney(journey);
              authJourneyTracker.trackContinueWithGoogleClicked(journey, {
                surface: 'electron_login'
              });
              ElectronAPI.login({
                type: 'google',
                authJourney: journey,
                hostedAuthParams
              });
            }}
          >
            <GoogleMark />
            Continue with Google
          </Button>
          <Button
            className='block w-full'
            size='lg'
            onClick={() => {
              const journey = beginAuthJourney('studio_login');
              const hostedAuthParams = buildHostedAuth0ExtParams(journey);
              setActiveAuthJourney(journey);
              authJourneyTracker.trackContinueWithEmailClicked(journey, {
                surface: 'electron_login'
              });
              ElectronAPI.login({
                type: 'email',
                authJourney: journey,
                hostedAuthParams
              });
            }}
          >
            Continue with Email
          </Button>
          <p className='text-muted'>
            Or{' '}
            <button
              {...STagNew(Sel.ElectronLogin, 'create-account')}
              onClick={() => {
                const journey = beginAuthJourney('studio_register');
                const hostedAuthParams = buildHostedAuth0ExtParams(journey);
                setActiveAuthJourney(journey);
                authJourneyTracker.trackCreateAccountClicked(journey, {
                  surface: 'electron_login'
                });
                // The desktop flow opens Auth0 directly and never mounts
                // /register, so it owns the canonical funnel entry event here.
                authJourneyTracker.trackRegistrationStarted(journey, {
                  surface: 'electron_login'
                });
                ElectronAPI.register({
                  authJourney: journey,
                  hostedAuthParams
                });
              }}
            >
              <span className='border-b-1 border-dashed'>Create An Account</span>
            </button>
          </p>
        </div>
        <p className='mt-[200px] text-secondary'>
          By signing in, you agree to our{' '}
          <button
            className='inline underline'
            onClick={() => {
              if (getCurrentVendor() === 'ptzoptics') {
                ElectronAPI.openUrl('https://tryiris.ai/hive-terms-of-service');
              } else {
                ElectronAPI.openUrl('https://tryiris.ai/terms');
              }
            }}
          >
            Terms
          </button>{' '}
          &{' '}
          <button
            className='underline'
            onClick={() => {
              if (getCurrentVendor() === 'ptzoptics') {
                ElectronAPI.openUrl('https://tryiris.ai/hive-privacy');
              } else {
                ElectronAPI.openUrl('https://tryiris.ai/privacy');
              }
            }}
          >
            Privacy Policy
          </button>
          .
        </p>
      </div>
    </Centered>
  );
};

export const ElectronLoginProgress = ({ redirectTarget }: { redirectTarget: string | null }) => {
  // The browser return page waits on the person: they open Iris from the
  // button or the OS prompt, or continue in the browser. Nothing here can
  // finish a Studio launch, so its time is a human wait — without one the
  // page's own launch clock expires and replaces it with the boot error.
  // Unmount (continue in browser, tab close) resumes bounded machine work.
  useEffect(() => enterBootHumanWait(), []);
  return (
    <Centered>
      <div className='flex flex-col items-center'>
        <img
          src={getCurrentVendorLogo()}
          alt={vendorName()}
          className='pointer-events-none w-[180px] select-none'
        />
        <div
          className='mt-40 flex flex-col items-center gap-16 text-center'
          {...DataSTag(Sel.ElectronLogin, 'handoff')}
        >
          <div>
            <h1 className='font-demibold'>Signing in...</h1>
            <p className='px-12 text-muted'>Please open {vendorName()} to continue signing in.</p>
          </div>

          <div className='my-6' />

          {redirectTarget && (
            <>
              <Button
                className='block w-full'
                size='lg'
                onClick={() => {
                  window.open(getElectronUrl(redirectTarget), '_self');
                }}
              >
                Open {vendorName()}
              </Button>
              <p className={clsx('flex h-16 items-center gap-4 text-sm text-secondary')}>
                <span className='mt-2'>Or</span>
                <a href='/' className='underline'>
                  continue in your browser
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </Centered>
  );
};
